import React, { useState, useEffect } from 'react';
import { Form, Input, Button, message, Spin } from 'antd';
import { UserOutlined, LockOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAppConfig } from '../contexts/AppConfigContext';
import { usePermission } from '../contexts/PermissionContext';
import ThemeToggle from '../components/ThemeToggle';
import { resolveUrl } from '../utils/url';
import './Login.css';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api\/v1$/, '') || '';

// Backend health check is only needed in the desktop (Tauri) app, where the
// bundled backend may still be starting up. In the web browser the backend is
// already running, so we skip the probe entirely.
const IS_DESKTOP = !!(window as any).__TAURI_INTERNALS__;

const Login: React.FC<{ title?: string; icon?: string; redirectTo?: string }> = ({ title, icon, redirectTo }) => {
  const [loading, setLoading] = useState(false);
  const [backendReady, setBackendReady] = useState(!IS_DESKTOP);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { appName } = useAppConfig();
  const displayName = title || appName;
  const { reload: reloadPermissions } = usePermission();

  const addLog = (msg: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const log = `[${timestamp}] ${msg}`;
    console.log('[Login]', log);
  };

  // Check backend health on mount (desktop app only)
  useEffect(() => {
    if (!IS_DESKTOP) return;

    addLog('组件挂载，开始检查后端服务');
    addLog(`API地址: ${API_BASE}`);

    const checkBackend = async () => {
      for (let i = 0; i < 30; i++) {
        try {
          addLog(`健康检查 #${i + 1}: 发送请求到 ${API_BASE}/health`);
          const res = await fetch(`${API_BASE}/health`, { method: 'GET' });
          addLog(`健康检查 #${i + 1}: 响应状态 ${res.status} ${res.statusText}`);

          if (res.ok) {
            const data = await res.json();
            addLog(`健康检查成功: ${JSON.stringify(data)}`);
            setBackendReady(true);
            return;
          }
        } catch (err: any) {
          addLog(`健康检查 #${i + 1} 失败: ${err.name} - ${err.message}`);
        }
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      addLog('后端服务启动超时（30次检查失败）');
      message.warning('后端服务启动超时，请查看下方日志详情');
    };
    checkBackend();
  }, []);

  const onFinish = async (values: { username: string; password: string }) => {
    addLog(`尝试登录: 用户名=${values.username}`);
    setLoading(true);
    try {
      const loginUrl = `${API_BASE}/api/v1/login?username=${encodeURIComponent(values.username)}&password=${encodeURIComponent(values.password)}`;
      addLog(`登录请求: POST ${loginUrl}`);

      const res = await fetch(loginUrl, { method: 'POST' });
      addLog(`登录响应: 状态 ${res.status} ${res.statusText}`);
      addLog(`响应头 Content-Type: ${res.headers.get('content-type')}`);

      if (!res.ok) {
        const err = await res.json();
        addLog(`登录失败: ${JSON.stringify(err)}`);
        throw new Error(err.detail || '登录失败');
      }

      const data = await res.json();
      addLog(`登录成功: token=${data.token?.substring(0, 20)}...`);
      addLog(`用户信息: ${JSON.stringify(data.user)}`);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      await reloadPermissions();
      message.success('登录成功');
      if (redirectTo) {
        navigate(redirectTo);
      } else {
        const redirect = searchParams.get('redirect');
        const standaloneAppId = process.env.REACT_APP_STANDALONE_APP_ID;
        if (standaloneAppId) {
          navigate(`/app/${standaloneAppId}/index`);
        } else {
          navigate(redirect ? decodeURIComponent(redirect) : '/dashboard');
        }
      }
    } catch (err: any) {
      addLog(`登录异常: ${err.name} - ${err.message}`);
      if (err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')) {
        message.error('无法连接到后端服务，请查看下方日志详情');
      } else {
        message.error(err.message || '登录失败');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!backendReady) {
    return (
      <div className={`login-page theme-${theme}`}>
        <div className="login-theme-toggle">
          <ThemeToggle />
        </div>
        <div className="login-container">
          <div className="login-header">
            {icon && (
              <div className="login-app-icon-wrap">
                {(icon.startsWith('http') || icon.startsWith('data:') || icon.startsWith('/uploads/') || icon.startsWith('/avatars/') || icon.startsWith('/app-icons/'))
                  ? <img src={resolveUrl(icon)} alt="" className="login-app-icon-img" />
                  : <span className="login-app-icon-emoji">{icon}</span>}
              </div>
            )}
            <h1 className={`login-logo${icon ? ' login-logo-app' : ''}`}>{displayName}</h1>
          </div>
          <div style={{ textAlign: 'center', padding: '16px 0' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <p style={{ marginTop: 8, color: 'var(--apple-text-secondary)', fontSize: 13 }}>正在连接后端服务...</p>
          </div>
          <Form className="login-form" name="login" onFinish={onFinish}>
            <Form.Item name="username" rules={[{ required: true, message: '请输入用户名' }]}>
              <Input prefix={<UserOutlined />} placeholder="用户名" size="large" className="apple-input" />
            </Form.Item>
            <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
              <Input.Password prefix={<LockOutlined />} placeholder="密码" size="large" className="apple-input" />
            </Form.Item>
            <Form.Item>
              <Button className="apple-btn-primary login-btn" htmlType="submit" block size="large" loading={loading}>登录</Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    );
  }

  return (
    <div className={`login-page theme-${theme}`}>
      <div className="login-theme-toggle">
        <ThemeToggle />
      </div>
      <div className="login-container">
        <div className="login-header">
          {icon && (
            <div className="login-app-icon-wrap">
              {(icon.startsWith('http') || icon.startsWith('data:') || icon.startsWith('/uploads/') || icon.startsWith('/avatars/') || icon.startsWith('/app-icons/'))
                ? <img src={resolveUrl(icon)} alt="" className="login-app-icon-img" />
                : <span className="login-app-icon-emoji">{icon}</span>}
            </div>
          )}
          <h1 className={`login-logo${icon ? ' login-logo-app' : ''}`}>{displayName}</h1>
        </div>

        <Form className="login-form" name="login" onFinish={onFinish}>
          <Form.Item name="username" rules={[{ required: true, message: '请输入用户名' }]}>
            <Input prefix={<UserOutlined />} placeholder="用户名" size="large" className="apple-input" />
          </Form.Item>
          <Form.Item name="password" rules={[{ required: true, message: '请输入密码' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="密码" size="large" className="apple-input" />
          </Form.Item>
          <Form.Item>
            <Button className="apple-btn-primary login-btn" htmlType="submit" block size="large" loading={loading}>登录</Button>
          </Form.Item>
        </Form>
      </div>

    </div>
  );
};

export default Login;
